import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  FileSearch,
  Loader2,
  AlertCircle,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';
import { useLocalStorageState } from '@/lib/useLocalStorageState';
import { ApiKeyGate } from '@/components/ApiKeyGate';

/**
 * Phase 57: 포스팅 품질 분석
 * 본문 + 타깃 키워드 → 글자수/키워드밀도(자체) + 형태소/비속어/SEO(Gemini)
 */
export function PostQuality() {
  const prefs = useUserPrefs();
  const hasKey = !!prefs.geminiApiKey;
  const [text, setText] = useLocalStorageState('postq.text', '');
  const [keyword, setKeyword] = useState('');

  const m = useMutation({
    mutationFn: () =>
      api.postQuality.analyze({
        text: text.trim(),
        targetKeyword: keyword.trim() || undefined,
        geminiApiKey: prefs.geminiApiKey.trim(),
      }),
    onError: (e: Error) => alert(`분석 실패: ${e.message}`),
  });

  const canSubmit = text.trim().length >= 50 && hasKey && !m.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <FileSearch className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">포스팅 품질 분석</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            NEW
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          원고 본문을 붙여넣으면 글자수·키워드 밀도·형태소·비속어·SEO 개선점을 진단합니다.
        </p>
      </header>

      <ApiKeyGate hasKey={hasKey} />

      {hasKey && (
        <Card>
          <CardTitle>원고 입력</CardTitle>
          <CardDescription>
            발행 전 원고를 붙여넣으세요. 글자수·키워드 밀도는 자체 계산, 형태소·비속어·SEO는 Gemini 분석.
          </CardDescription>
          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) m.mutate();
            }}
          >
            <label className="block">
              <span className="text-xs text-gray-400">타깃 키워드 (선택 — 밀도 계산용)</span>
              <Input
                className="mt-1"
                placeholder="예: 제주도 여행 코스"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400">
                원고 본문 ({text.trim().length}자 — 최소 50자)
              </span>
              <textarea
                rows={10}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white"
                placeholder="블로그 원고 전체를 붙여넣으세요..."
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>
            <div className="flex items-center gap-3">
              <Button type="submit" variant="gold" disabled={!canSubmit}>
                {m.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> 분석 중…
                  </>
                ) : (
                  <>
                    <FileSearch className="h-4 w-4" /> 품질 분석
                  </>
                )}
              </Button>
              {m.isPending && <span className="text-xs text-gray-500">Gemini 분석 ~5–15초</span>}
            </div>
          </form>

          {m.isError && (
            <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <AlertCircle className="inline h-4 w-4 mr-1" />
              {(m.error as Error).message}
            </p>
          )}
        </Card>
      )}

      {m.isPending && (
        <Card>
          <Skeleton className="h-6 w-48" />
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </Card>
      )}

      {m.data && <ResultSection data={m.data} />}
    </div>
  );
}

function ResultSection({
  data,
}: {
  data: Awaited<ReturnType<typeof api.postQuality.analyze>>;
}) {
  const { stats, analysis } = data;
  return (
    <div className="space-y-4">
      {/* 종합 점수 */}
      <Card className="border-gold/40 bg-gradient-to-br from-gold/5 to-transparent">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>종합 품질 점수</CardTitle>
            <CardDescription className="mt-1">"{analysis.topicSummary}"</CardDescription>
          </div>
          <div className="text-right">
            <div className={cn('text-4xl font-extrabold', scoreColor(analysis.overallScore))}>
              {analysis.overallScore}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">/ 100</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <ScoreBar label="주제 일관성" score={analysis.topicConsistency} />
          <ScoreBar label="가독성" score={analysis.readabilityScore} />
        </div>
      </Card>

      {/* 통계 */}
      <Card>
        <CardTitle className="text-base">📊 글 통계</CardTitle>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="글자수(공백포함)" value={stats.charsWithSpace.toLocaleString()} />
          <Stat label="글자수(공백제외)" value={stats.charsNoSpace.toLocaleString()} />
          <Stat label="문장 수" value={String(stats.sentences)} />
          <Stat label="문단 수" value={String(stats.paragraphs)} />
        </div>
        {stats.targetKeyword && (
          <div className="mt-3 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2.5 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-gray-300">
              타깃 키워드 <strong className="text-gold">"{stats.targetKeyword}"</strong> 출현{' '}
              <strong className="text-white">{stats.keywordCount}회</strong>
            </span>
            <span className="text-xs">
              밀도{' '}
              <strong
                className={cn(
                  stats.keywordDensity >= 0.5 && stats.keywordDensity <= 3
                    ? 'text-emerald-300'
                    : 'text-amber-300',
                )}
              >
                {stats.keywordDensity}%
              </strong>
              <span className="text-gray-500 ml-1">(권장 0.5~3%)</span>
            </span>
          </div>
        )}
      </Card>

      {/* 형태소 */}
      {analysis.morphemes.length > 0 && (
        <Card>
          <CardTitle className="text-base">🔤 자주 쓴 단어 (형태소 빈도)</CardTitle>
          <div className="mt-3 flex flex-wrap gap-2">
            {analysis.morphemes.map((m, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-800 bg-gray-950 px-3 py-1 text-sm"
              >
                <span className="text-white">{m.word}</span>
                <span className="text-xs text-gold font-bold">{m.count}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* 비속어 */}
      {analysis.profanity.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardTitle className="text-base flex items-center gap-2 text-red-200">
            <AlertTriangle className="h-4 w-4" /> 비속어/부적절 표현 검출
          </CardTitle>
          <div className="mt-2 flex flex-wrap gap-2">
            {analysis.profanity.map((p, i) => (
              <span key={i} className="rounded-full bg-red-500/15 px-2.5 py-1 text-xs text-red-200">
                {p}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* SEO 제안 */}
      <Card className="border-emerald-400/30 bg-emerald-400/5">
        <CardTitle className="text-base text-emerald-200">🎯 SEO 개선 제안</CardTitle>
        <ol className="mt-3 space-y-2">
          {analysis.seoSuggestions.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-emerald-100/90">
              <span className="tabular text-xs font-bold text-emerald-300 mt-0.5 w-5">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className={cn('font-bold', scoreColor(score))}>{score}</span>
      </div>
      <div className="mt-1.5 h-2 w-full rounded-full bg-gray-900 overflow-hidden">
        <div
          className={cn('h-full transition-[width] duration-500', scoreBarBg(score))}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="tabular text-lg font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}

function scoreColor(s: number): string {
  if (s >= 80) return 'text-emerald-300';
  if (s >= 60) return 'text-gold';
  if (s >= 40) return 'text-amber-300';
  return 'text-red-300';
}
function scoreBarBg(s: number): string {
  if (s >= 80) return 'bg-emerald-400';
  if (s >= 60) return 'bg-gold';
  if (s >= 40) return 'bg-amber-400';
  return 'bg-red-400';
}
